/**
 * Real-time takes hook for the Recording Studio.
 */

import { useState, useEffect, useMemo } from "react";
import { subscribeTakes } from "@/lib/firestore/takes";
import type { Take } from "@/types";

const MAX_AUDIO_DURATION = 20 * 60 * 60; // 20 hours in seconds

export function useTakes(projectId: string | null) {
  const [takes, setTakes] = useState<Take[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) { setTakes([]); setLoading(false); return; }
    setLoading(true);
    return subscribeTakes(
      projectId,
      (data) => { setTakes(data); setLoading(false); setError(null); },
      (err) => { setError(err.message); setLoading(false); },
    );
  }, [projectId]);

  const computed = useMemo(() => {
    const readyTakes = takes.filter((t) => t.status === "ready");
    const audioTakes = readyTakes.filter((t) => t.type !== "text_upload");
    const totalDurationSeconds = audioTakes.reduce((sum, t) => sum + t.duration_seconds, 0);
    return {
      readyTakes,
      readyCount: readyTakes.length,
      totalCount: takes.length,
      totalDurationSeconds,
      isOverLimit: totalDurationSeconds > MAX_AUDIO_DURATION,
      isNearLimit: totalDurationSeconds > MAX_AUDIO_DURATION * 0.9,
      nextOrder: takes.length > 0 ? Math.max(...takes.map((t) => t.order)) + 1 : 0,
    };
  }, [takes]);

  return { takes, loading, error, ...computed };
}
